/*
 * <product-lightbox> — full-screen viewer for the product gallery. Any button with
 * data-lightbox-open="<index>" opens it at that image. The slides are stacked and
 * the current one fades in; a horizontal swipe on touch screens changes image too.
 */
const SWIPE_DISTANCE = 50

class ProductLightbox extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog')
    this.stage = this.querySelector('[data-lightbox-stage]')
    this.slides = [...this.querySelectorAll('[data-lightbox-slide]')]
    this.thumbnails = [...this.querySelectorAll('[data-lightbox-go]')]
    this.currentIndex = 0

    this.handleDocumentClick = this.handleDocumentClick.bind(this)
    document.addEventListener('click', this.handleDocumentClick)
    this.addEventListener('click', this.handleClick.bind(this))
    this.dialog.addEventListener('keydown', this.handleKeydown.bind(this))
    this.stage.addEventListener('pointerdown', this.handlePointerDown.bind(this))
    this.stage.addEventListener('pointerup', this.handlePointerUp.bind(this))
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.handleDocumentClick)
  }

  handleDocumentClick(event) {
    const opener = event.target.closest('[data-lightbox-open]')
    if (!opener) return

    this.open(Number(opener.dataset.lightboxOpen))
  }

  handleClick(event) {
    // Clicking the empty space around an image closes the viewer, like the close button
    const isBackground = event.target.matches('[data-lightbox-slide]')
    if (isBackground || event.target.closest('[data-lightbox-close]')) {
      this.dialog.close()
      return
    }

    const stepButton = event.target.closest('[data-lightbox-step]')
    if (stepButton) {
      this.goTo(this.currentIndex + Number(stepButton.dataset.lightboxStep))
      return
    }

    const thumbnail = event.target.closest('[data-lightbox-go]')
    if (thumbnail) this.goTo(Number(thumbnail.dataset.lightboxGo))
  }

  handleKeydown(event) {
    const step = { ArrowRight: 1, ArrowLeft: -1 }[event.key]
    if (!step) return

    event.preventDefault()
    this.goTo(this.currentIndex + step)
  }

  handlePointerDown(event) {
    if (event.pointerType !== 'touch') return
    this.swipeStartX = event.clientX
  }

  handlePointerUp(event) {
    if (event.pointerType !== 'touch' || this.swipeStartX === undefined) return

    const distance = event.clientX - this.swipeStartX
    this.swipeStartX = undefined
    if (Math.abs(distance) < SWIPE_DISTANCE) return

    // Swiping left shows the next image, like turning a page
    this.goTo(this.currentIndex + (distance < 0 ? 1 : -1))
  }

  open(index) {
    this.goTo(index)
    this.dialog.showModal()
  }

  goTo(index) {
    // Wraps around, so "next" on the last image returns to the first
    const slideCount = this.slides.length
    this.currentIndex = (index + slideCount) % slideCount

    this.slides.forEach((slide, slideIndex) => {
      slide.toggleAttribute('data-current', slideIndex === this.currentIndex)
    })
    this.thumbnails.forEach((thumbnail, thumbnailIndex) => {
      thumbnail.setAttribute('aria-current', thumbnailIndex === this.currentIndex)
    })
    this.thumbnails[this.currentIndex]?.scrollIntoView({ block: 'nearest', inline: 'nearest' })
  }
}

if (!customElements.get('product-lightbox')) {
  customElements.define('product-lightbox', ProductLightbox)
}
